import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Table, Tag, Empty, Typography, Skeleton } from 'antd';
import { CheckCircle } from 'lucide-react';
import { alertApi } from '../services/alertApi';
import dayjs from 'dayjs';
import {
  alertStatusLabel,
  alertStatusTagColor,
  isFiringOnlyAlertState,
  mergeAlertEvaluationStates,
  normalizeAlertEvaluationState,
} from '../utils/alertDisplayHelpers';

const { Text } = Typography;

const FiringAlertList = ({ scopeMonitored: _scopeMonitored, className, layout = 'rail' }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchSeqRef = useRef(0);

  const fetchFiringAlerts = useCallback(async () => {
    const fetchId = ++fetchSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      const response = await alertApi.getFiringAlerts();
      if (fetchId !== fetchSeqRef.current) return;
      if (response.status === 'success' && Array.isArray(response.alerts)) {
        setAlerts(response.alerts);
      } else {
        setAlerts([]);
      }
    } catch (err) {
      if (fetchId !== fetchSeqRef.current) return;
      console.error('Error fetching firing alerts:', err);
      setError(err.message);
      setAlerts([]);
    } finally {
      if (fetchId === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchFiringAlerts();
    const interval = setInterval(fetchFiringAlerts, 30000);
    return () => {
      fetchSeqRef.current += 1;
      clearInterval(interval);
    };
  }, [fetchFiringAlerts]);

  const formatTime = (timeString) => {
    if (!timeString) return 'Unknown';
    try {
      const time = dayjs(timeString);
      return time.format('MMM DD, YYYY HH:mm:ss');
    } catch (e) {
      return timeString;
    }
  };

  const getAlertName = (alert) => {
    const labels = alert.labels || {};
    if (alert.name === 'alertname' || alert.name === 'rulename') {
      return labels.rulename || labels.alertname || 'Unknown Alert';
    }
    if (alert.name && alert.name.trim() !== '') {
      return alert.name;
    }
    return labels.rulename || labels.alertname || 'Unknown Alert';
  };

  const tableData = useMemo(() => {
    // /firing-alerts already returns currently active alerts. Do not filter by
    // activeAt age — that timestamp is the episode start and can be older than 48h
    // while the alert is still firing (or missing for some Grafana rule states).
    const firingAlerts = alerts.filter((alert) => {
      if (!isFiringOnlyAlertState(alert.state)) {
        return false;
      }
      const alertName = getAlertName(alert);
      return Boolean(
        alertName
        && alertName.trim() !== ''
        && alertName !== 'alertname'
        && alertName !== 'rulename',
      );
    });

    const alertMap = {};
    firingAlerts.forEach((alert) => {
      const alertName = getAlertName(alert).trim();
      const activeAtString = alert.activeAt;
      const activeAt = activeAtString ? dayjs(activeAtString) : null;

      const evaluationState = normalizeAlertEvaluationState(alert.state);

      if (!alertMap[alertName]) {
        alertMap[alertName] = {
          name: alertName,
          state: evaluationState,
          status: alertStatusLabel(evaluationState),
          startedRaw: activeAt,
          started: formatTime(activeAtString),
          count: 1,
        };
      } else {
        const mergedState = mergeAlertEvaluationStates(
          alertMap[alertName].state,
          evaluationState,
        );
        alertMap[alertName].state = mergedState;
        alertMap[alertName].status = alertStatusLabel(mergedState);
        if (activeAt && (!alertMap[alertName].startedRaw || activeAt.isAfter(alertMap[alertName].startedRaw))) {
          alertMap[alertName].startedRaw = activeAt;
          alertMap[alertName].started = formatTime(activeAtString);
        }
        alertMap[alertName].count += 1;
      }
    });

    return Object.values(alertMap)
      .sort((a, b) => {
        if (!a.startedRaw && !b.startedRaw) return 0;
        if (!a.startedRaw) return 1;
        if (!b.startedRaw) return -1;
        return b.startedRaw.valueOf() - a.startedRaw.valueOf();
      })
      .map((alert, index) => ({
        key: index,
        ...alert,
      }));
  }, [alerts]);

  const columns = [
    {
      title: 'Alert Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => (
        <div className="dashboard-home__fa-cell-name" title={text}>
          {text}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 88,
      align: 'center',
      render: (status, record) => (
        <Tag color={alertStatusTagColor(record.state)} className="dashboard-home__fa-tag">
          {status}
        </Tag>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'started',
      key: 'started',
      width: 168,
      sorter: (a, b) => {
        if (!a.startedRaw) return 1;
        if (!b.startedRaw) return -1;
        return dayjs(a.startedRaw).valueOf() - dayjs(b.startedRaw).valueOf();
      },
      defaultSortOrder: 'descend',
      render: (text) => (
        <Text className="dashboard-home__fa-cell-time">
          {text}
        </Text>
      ),
    },
  ];

  const useScroll = layout === 'grid' && tableData.length > 4;

  const rootClass = [
    'dashboard-home__firing-alerts-root',
    layout === 'grid' && 'dashboard-home__firing-alerts-root--grid',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (loading) {
    return (
      <div className={rootClass}>
        <div className="dashboard-home__fa-skeleton" aria-busy="true" aria-label="Loading alerts">
          <Skeleton active title={{ width: '55%' }} paragraph={{ rows: 6, width: ['100%', '100%', '92%', '100%', '88%', '100%'] }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={rootClass}>
        <Empty
          className="dashboard-home__fa-empty dashboard-home__fa-empty--error"
          description={
            <span className="dashboard-home__fa-empty-desc">
              Failed to load alerts: {error}
            </span>
          }
        />
      </div>
    );
  }

  if (!alerts || alerts.length === 0 || tableData.length === 0) {
    return (
      <div className={rootClass}>
        <div
          className={`dashboard-home__fa-empty-clear${layout === 'grid' ? ' dashboard-home__fa-empty-clear--grid' : ''}`}
        >
          <div className="dashboard-home__fa-empty-icon" aria-hidden>
            <CheckCircle size={22} strokeWidth={2} className="dashboard-home__fa-empty-check" />
          </div>
          <div className="dashboard-home__fa-empty-title">
            All clear
          </div>
          <div className="dashboard-home__fa-empty-sub">
            No firing alerts. Systems running smoothly.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
        className="dashboard-home__fa-table"
        tableLayout="fixed"
        scroll={useScroll ? { y: 240, x: '100%' } : undefined}
      />
    </div>
  );
};

export default FiringAlertList;